import { useEffect } from "react";
import { BadgeCheck, CalendarCheck, FileText } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const FACHBEREICHE = [
  {
    nr: "01",
    text: "Maßnahmen zur Aktivierung und beruflichen Eingliederung nach § 45 Absatz 1 Satz 1 Nummer 1 bis 5 des Dritten Buches Sozialgesetzbuch",
  },
  {
    nr: "04",
    text: "Maßnahmen der beruflichen Weiterbildung nach dem Vierten Abschnitt des Dritten Kapitels des Dritten Buches Sozialgesetzbuch",
  },
];

export default function AzavZulassung() {
  useEffect(() => {
    document.title = "AZAV-Zulassung · Zertifizierter Bildungsträger | Dickies-Campus";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = "Die Dickies-Campus GmbH ist nach § 178 SGB III und AZAV zugelassener Bildungsträger. Registrier-Nr. 0184.1-10141-AZAV-T · Fachbereiche 01 und 04.";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24" data-testid="azav-page">
        <span className="eyebrow">Qualität &amp; Zulassung</span>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl font-semibold text-sand-800 tracking-tight">
          AZAV-zugelassener Bildungsträger
        </h1>
        <p className="mt-4 text-sand-700 text-base leading-relaxed">
          Die Dickies-Campus GmbH ist durch die APV-Zertifizierungs GmbH als Träger nach
          § 178 SGB III und der Akkreditierungs- und Zulassungsverordnung Arbeitsförderung (AZAV)
          zugelassen. Damit können förderfähige Weiterbildungen über Bildungsgutschein der
          Agentur für Arbeit oder des Jobcenters bei uns absolviert werden.
        </p>

        <div className="mt-10 rounded-2xl border border-sand-800/10 bg-white/70 p-6 sm:p-8" data-testid="azav-zulassung-box">
          <div className="flex items-center gap-2 text-terra font-bold text-xs uppercase tracking-widest">
            <BadgeCheck className="w-4 h-4" /> Angaben zur Zulassung
          </div>
          <dl className="mt-5 space-y-4 text-base">
            <div>
              <dt className="font-semibold text-sand-800">Zugelassener Träger</dt>
              <dd className="text-sand-700">Dickies-Campus GmbH, Neuenhausplatz 73, 40699 Erkrath — zugelassen nach § 178 SGB III und AZAV</dd>
            </div>
            <div>
              <dt className="font-semibold text-sand-800">Zertifikats-Registrier-Nr.</dt>
              <dd className="text-sand-700" data-testid="azav-regnr">0184.1-10141-AZAV-T</dd>
            </div>
            <div>
              <dt className="font-semibold text-sand-800">Zertifizierungsstelle</dt>
              <dd className="text-sand-700">APV-Zertifizierungs GmbH, Kassel</dd>
            </div>
            <div>
              <dt className="font-semibold text-sand-800">Zulassungszeitraum</dt>
              <dd className="text-sand-700 flex items-center gap-2">
                <CalendarCheck className="w-4 h-4 text-sage-dark shrink-0" />
                14.02.2024 bis 13.02.2029 (Erstzulassung am 14.02.2019)
              </dd>
            </div>
          </dl>
        </div>

        <h2 className="mt-12 font-heading text-2xl text-sand-800 font-semibold">Geltungsbereich · Fachbereiche</h2>
        <div className="mt-5 space-y-4">
          {FACHBEREICHE.map((f) => (
            <div key={f.nr} className="flex gap-4 rounded-xl border border-sand-800/10 bg-white/70 p-5" data-testid={`azav-fachbereich-${f.nr}`}>
              <span className="font-heading text-2xl font-semibold text-terra shrink-0">{f.nr}</span>
              <p className="text-sand-700 text-base leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sand-700 text-base leading-relaxed">
          Grundlage unserer Arbeit ist unser Unternehmensleitbild:&nbsp;
          <a href="/leitbild" data-testid="azav-link-leitbild" className="text-terra font-semibold underline underline-offset-4 hover:text-sand-800">
            Unser Leitbild lesen →
          </a>
        </p>

        <div className="mt-12 rounded-2xl border border-sand-800/10 bg-white p-6 sm:p-8" data-testid="azav-apv-zeichen">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <img
              src="/apv-zeichen-azav.png"
              alt="Zertifizierungszeichen der APV-Zertifizierungs GmbH"
              className="w-36 h-auto shrink-0"
              data-testid="apv-zeichen-img"
            />
            <div className="text-sand-700 text-base leading-relaxed">
              <div className="font-semibold text-sand-800">Dickies-Campus GmbH</div>
              <div className="mt-1">
                Zugelassener Träger nach dem Recht der Arbeitsförderung (§ 178 SGB III / AZAV)
              </div>
              <div className="mt-1">
                Geltungsbereich: Fachbereiche 01 und 04 · Zertifikats-Nr.: 0184.1-10141-AZAV-T
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 rounded-2xl bg-sage/20 border border-sage/40 p-6 sm:p-8">
          <div className="flex items-center gap-2 text-sand-800 font-semibold">
            <FileText className="w-5 h-5 text-sage-dark" /> Was bedeutet das für Sie?
          </div>
          <p className="mt-3 text-sand-700 text-base leading-relaxed">
            Für zugelassene Maßnahmen können die Agentur für Arbeit oder das Jobcenter die
            Weiterbildungskosten über einen Bildungsgutschein übernehmen. Ob Ihre Wunsch-Weiterbildung
            förderfähig ist und wie Sie den Gutschein erhalten, klären wir gemeinsam in der
            kostenlosen Beratung — ehrlich und individuell.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
